import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useHotAndCold } from '../context/HotAndColdContext';
import Header from './Header';
import SEO from './SEO';
import { ChevronRight, Sparkles } from 'lucide-react';

const HotAndColdArchivePage = () => {
  const { puzzles, loading } = useHotAndCold();

  // Group puzzles by Month Year
  const groupedPuzzles = useMemo(() => {
    const groups = {};
    puzzles.forEach(puzzle => {
      const date = new Date(puzzle.date);
      const key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(puzzle);
    });
    return groups;
  }, [puzzles]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Hot and Cold Archive" 
        description="Browse past Hot and Cold hints and answers. Access the complete archive of Hot and Cold daily helpers."
        url={window.location.origin + '/hot-and-cold-archive'}
      />
      
      <Header date={new Date().toISOString()} title="Hot and Cold Archive" gameName="Hot and Cold Archive" />

      <main className="flex-grow max-w-4xl mx-auto px-4 w-full pb-20">
        <div className="text-center mb-12">
           <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Hot and Cold Archive</h2>
           <p className="text-gray-600 text-lg mb-6">Browse our collection of daily Hot and Cold guides.</p>
           
           {/* Cross-link to other archive */}
           <Link 
              to="/archive"
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-yellow/20 text-yellow-900 rounded-lg font-bold hover:bg-brand-yellow/30 transition-colors text-sm"
           >
             Looking for Pocket Grids Archives? <ChevronRight className="w-4 h-4" />
           </Link>
        </div>


        <div className="space-y-12">
          {Object.entries(groupedPuzzles).map(([monthYear, monthPuzzles]) => (
            <div key={monthYear} className="animate-fade-in-up">
              <h3 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-6 border-b border-gray-200 pb-2">
                {monthYear} Hot and Cold Answers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {monthPuzzles.map((puzzle) => {
                   const pDate = new Date(puzzle.date);
                   const dayName = pDate.toLocaleDateString('en-US', { weekday: 'long' });
                   const dayNum = pDate.getDate();
                   
                   return (
                    <Link 
                      key={puzzle.date} 
                      to={`/hot-and-cold/hot-and-cold-${puzzle.puzzleNumber || puzzle.date}`}
                      className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-blue hover:shadow-md transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-brand-blue/10 text-brand-blue font-bold text-xl w-12 h-12 rounded-lg flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                          {dayNum}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{puzzle.title}</div>
                          <div className="text-sm text-gray-500 font-medium">{dayName}</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue transition-colors" />
                    </Link>
                   );
                })}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default HotAndColdArchivePage;








